import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import ConnectionLog from './ConnectionLog';
import ContentTab, { confirmLeaveContentTab } from './AdminContentTab';
import { useIsMobile } from '../hooks/useIsMobile';

// ── Admin API calls ───────────────────────────────────────────────────────────
const adminApi = {
  getUsers:          (p) => api.get('/api/admin/users', { params: p }),
  getUser:           (id) => api.get(`/api/admin/users/${id}`),
  createUser:        (body) => api.post('/api/admin/users', body),
  updateUser:        (id, body) => api.patch(`/api/admin/users/${id}`, body),
  setUserPaperPeriod:(id, days) => api.put(`/api/admin/users/${id}/paper-period`, { days }),
  deleteUser:        (id) => api.delete(`/api/admin/users/${id}`),
  getContainers:     () => api.get('/api/admin/containers'),
  stopUserContainers:(id) => api.post(`/api/admin/containers/${id}/stop`),
  stopAllContainers: () => api.post('/api/admin/containers/stop-all'),
  recreateBot:       (id) => api.post(`/api/admin/bots/${id}/recreate`),
  recreateBots:      () => api.post('/api/admin/bots/recreate'),
  getResults:        (p) => api.get('/api/admin/results', { params: p }),
  getJobs:           () => api.get('/api/admin/jobs'),
  cancelJob:         (id) => api.post(`/api/admin/jobs/${encodeURIComponent(id)}/cancel`),
  restartJob:        (id) => api.post(`/api/admin/jobs/${encodeURIComponent(id)}/restart`),
  pauseJob:          (id) => api.post(`/api/admin/jobs/${encodeURIComponent(id)}/pause`),
  resumeJob:         (id) => api.post(`/api/admin/jobs/${encodeURIComponent(id)}/resume`),
  urgentJob:         (id) => api.post(`/api/admin/jobs/${encodeURIComponent(id)}/urgent`, { on: true }),
  normalJob:         (id) => api.post(`/api/admin/jobs/${encodeURIComponent(id)}/urgent`, { on: false }),
  getActiveStrategy: ()        => api.get('/api/admin/active-strategy'),
  setActiveStrategy: (name, params) => api.put('/api/admin/active-strategy', { name, params }),
  getBroadcastRecipients: () => api.get('/api/admin/broadcast/recipients'),
  sendBroadcast:          (body) => api.post('/api/admin/broadcast', body),
  getBroadcastSenders:    () => api.get('/api/admin/broadcast/senders'),
  getProductionStrategies:    ()  => api.get('/api/admin/production-strategies'),
  deleteProductionStrategy:   (id) => api.delete(`/api/admin/production-strategies/${id}`),
  setActiveStrategyByProdId:  (id) => api.put('/api/admin/active-strategy', { production_strategy_id: id }),
  getAccountMargin:   ()     => api.get('/api/admin/system-config/account-margin'),
  setAccountMargin:   (body) => api.put('/api/admin/system-config/account-margin', body),
  clearAccountMargin: ()     => api.delete('/api/admin/system-config/account-margin'),
  getSavedStrategies: ()       => api.get('/api/admin/saved-strategies'),
  setUserBotConfig:   (id, body) => api.put(`/api/admin/users/${id}/bot-config`, body),
  applyUserBotConfig: (id)       => api.post(`/api/admin/users/${id}/bot-apply`),
  getUserBotPositions: (id)      => api.get(`/api/admin/users/${id}/bot-positions`),
  sellUserBotPosition: (id, t)   => api.post(`/api/admin/users/${id}/bot-positions/${encodeURIComponent(t)}/sell`),
  cancelUserBotCommand: (id, c)  => api.delete(`/api/admin/users/${id}/bot-commands/${c}`),
  getTransition:        ()       => api.get('/api/admin/system-config/transition'),
  setTransition:        (mode)   => api.put('/api/admin/system-config/transition', { transition_mode: mode }),
  // Перестворює ботів по черзі (~35 с на кожного) — 30 с загального ліміту не вистачає:
  // сторінка показувала «timeout», хоча сервер доводив перемикання до кінця.
  applyStrategyToBots:  ()       => api.post('/api/admin/bots/apply-strategy', null, { timeout: 300000 }),
};

// Позиції бота користувача + «Продати» (команда боту, виконується на
// найближчому барі торгової сесії; гроші бот бере в роботу).
function BotPositionsModal({ u, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => {
    adminApi.getUserBotPositions(u.id)
      .then(r => setRows(r.data.positions || []))
      .catch(e => setErr(e.response?.data?.detail || 'Не вдалося завантажити позиції'));
  }, [u.id]);
  useEffect(() => { load(); }, [load]);

  const sell = async (t) => {
    if (!window.confirm(`Продати всю позицію ${t} бота користувача ${u.name || u.username}? `
      + 'Бот продасть на найближчому барі торгової сесії.')) return;
    setBusy(t);
    try { await adminApi.sellUserBotPosition(u.id, t); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Не вдалося'); }
    finally { setBusy(''); }
  };
  const cancel = async (t, cid) => {
    setBusy(t);
    try { await adminApi.cancelUserBotCommand(u.id, cid); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Не вдалося'); }
    finally { setBusy(''); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
         onClick={onClose}>
      <div style={{ ...S.card, maxWidth: 760, width: '100%', maxHeight: '80vh', overflow: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={S.cardTitle}>Позиції бота — {u.name || u.username}</div>
          <button style={S.btn()} onClick={onClose}>✕</button>
        </div>
        {err && <div style={{ color: '#EF4444' }}>{err}</div>}
        {!rows && !err && <div style={{ color: '#6B7280' }}>Завантаження…</div>}
        {rows && rows.length === 0 && <div style={{ color: '#6B7280' }}>У бота немає відкритих позицій.</div>}
        {rows && rows.length > 0 && (
          <table style={UT.table}>
            <thead><tr>{['Тікер', 'Кількість', 'Ціна купівлі', 'Куплено', 'Стратегія', ''].map(h =>
              <th key={h} style={UT.th}>{h}</th>)}</tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.ticker}>
                <td style={UT.td}><b>{r.ticker}</b></td>
                <td style={UT.td}>{r.quantity}</td>
                <td style={UT.td}>{r.avg_price != null ? `$${r.avg_price}` : '—'}</td>
                <td style={UT.td}>{(r.first_buy || '').slice(0, 16).replace('T', ' ') || '—'}</td>
                <td style={UT.td}>{r.strategy || <span style={UT.dash}>—</span>}</td>
                <td style={UT.td}>
                  {r.sell_command
                    ? <>
                        <span style={UT.pill('yellow')}>Чекає продажу</span>{' '}
                        <button style={S.btn()} disabled={busy === r.ticker}
                          onClick={() => cancel(r.ticker, r.sell_command.id)}>Скасувати</button>
                      </>
                    : <button style={S.btn('danger')} disabled={busy === r.ticker}
                        onClick={() => sell(r.ticker)}>Продати</button>}
                </td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Перехід на іншу стратегію: що робити з акціями, купленими за попередньою.
const TRANSITION_OPTIONS = [
  { value: 'by_old',   label: 'Продавати по попередній' },
  { value: 'by_new',   label: 'Продавати по новій' },
  { value: 'sell_all', label: 'Продати все' },
];

// Стратегія / плече / перехід бота для одного користувача (23.10.2026).
// Значення зберігається одразу при виборі; запущений бот отримує його при
// наступному старті або після «Застосувати».
function UserBotCells({ u, strategies, rungs, onChanged }) {
  const cfg = u.bot_config || {};
  const [busy, setBusy] = useState(false);
  const [showPos, setShowPos] = useState(false);
  const running = u.containers?.bot?.status === 'running';

  const save = async (body) => {
    setBusy(true);
    try { await adminApi.setUserBotConfig(u.id, body); onChanged(); }
    catch (e) { alert(e.response?.data?.detail || 'Не вдалося зберегти'); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (!window.confirm('Перезапустити бота з новими налаштуваннями? '
      + 'Шлюз IBKR не чіпається (без 2FA), але бот перечитає історію — '
      + 'краще робити поза торговою сесією.')) return;
    setBusy(true);
    try {
      const r = await adminApi.applyUserBotConfig(u.id);
      const st = r.data?.status;
      if (st === 'not_running') alert(r.data.detail);
      else if (st === 'unchanged') alert('Бот уже працює з цими налаштуваннями.');
      onChanged();
    } catch (e) { alert(e.response?.data?.detail || 'Не вдалося застосувати'); }
    finally { setBusy(false); }
  };

  const stratValue = cfg.admin_strategy?.production_strategy_id ?? '';
  const levValue = cfg.admin_leverage_cap == null ? '' : String(Number(cfg.admin_leverage_cap));
  const sel = { ...UT.pill('gray'), border: '1px solid #E5E7EB', cursor: 'pointer',
                opacity: busy ? 0.5 : 1 };

  return <>
    <td style={UT.td}>
      <select value={cfg.admin_transition_mode || ''} disabled={busy} style={sel}
        onChange={e => save({ transition_mode: e.target.value === '' ? null : e.target.value })}
        title="Що робити з акціями, купленими за попередньою стратегією (виняток для цього користувача)">
        <option value="">Як у всіх ({(TRANSITION_OPTIONS.find(o => o.value === cfg.global_transition_mode)
          || TRANSITION_OPTIONS[0]).label.toLowerCase()})</option>
        {TRANSITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </td>
    <td style={UT.td}>
      {/* Дві строки: список + кнопки в ряд, під ними стан бота одним рядком. */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <select value={stratValue} disabled={busy} style={sel}
          onChange={e => save({ production_strategy_id: e.target.value === '' ? null : Number(e.target.value) })}>
          <option value="">Як у всіх ({cfg.global_strategy?.display_name || 'загальна'})</option>
          {strategies.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
        </select>
        {running && <button style={{ ...S.btn(cfg.pending ? 'primary' : 'default'), padding: '2px 10px' }}
          disabled={busy} onClick={apply}
          title="Перезапустити запущеного бота з призначеною стратегією, плечем і режимом переходу">
          Застосувати</button>}
        <button style={{ ...S.btn(), padding: '2px 10px' }} onClick={() => setShowPos(true)}
          title="Відкриті позиції бота; кнопка «Продати» — продаж через бота">Позиції</button>
      </div>
      {(cfg.applied || cfg.pending) && (
        <div style={{ fontSize: 12, lineHeight: '16px', color: '#6B7280' }}>
          {cfg.applied && `Бот: ${cfg.applied.display_name || '—'}`}
          {cfg.applied && cfg.pending && ' · '}
          {cfg.pending && <span style={{ color: '#B45309' }}>Очікує застосування</span>}
        </div>
      )}
      {showPos && <BotPositionsModal u={u} onClose={() => setShowPos(false)} />}
    </td>
    <td style={UT.td}>
      <select value={levValue} disabled={busy} style={sel}
        onChange={e => save({ leverage_cap: e.target.value === '' ? null : Number(e.target.value) })}>
        <option value="">Як у всіх ({Number(cfg.global_leverage) > 1
          ? `x${Number(cfg.global_leverage)}` : 'без плеча'})</option>
        <option value="1">Без плеча</option>
        {rungs.map(r => <option key={r} value={String(r)}>x{r}</option>)}
      </select>
    </td>
  </>;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  wrap: { padding: '24px 32px', maxWidth: 1280, margin: '0 auto' },
  title: { fontSize: 24, fontWeight: 700, color: '#101828', marginBottom: 4 },
  sub:   { fontSize: 14, color: '#6B7280', marginBottom: 24 },

  tabs: { display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid #E5E7EB', paddingBottom: 8 },
  tab: (a) => ({
    padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500,
    background: a ? '#163382' : 'transparent',
    color: a ? '#fff' : '#6B7280',
  }),

  card: { background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 16 },
  cardTitle: { fontSize: 16, fontWeight: 600, color: '#101828', marginBottom: 12 },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' },
  td: { padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #F3F4F6' },
  // Перша колонка не їде при горизонтальному скролі (телефон: таблиця ширша
  // за екран — назва рядка має лишатись видимою).
  stickyCol: { position: 'sticky', left: 0, zIndex: 1, background: '#fff',
               boxShadow: 'inset -1px 0 0 #E5E7EB, 4px 0 6px -4px rgba(15,23,31,0.15)' },

  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    background: color === 'green' ? '#DCFCE7' : color === 'red' ? '#FEE2E2' : color === 'yellow' ? '#FEF9C3' : '#F3F4F6',
    color: color === 'green' ? '#166534' : color === 'red' ? '#991B1B' : color === 'yellow' ? '#92400E' : '#374151',
  }),

  btn: (variant = 'default') => ({
    padding: '6px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500,
    background: variant === 'danger' ? '#FEE2E2' : variant === 'primary' ? '#163382' : '#F3F4F6',
    color: variant === 'danger' ? '#991B1B' : variant === 'primary' ? '#fff' : '#374151',
    transition: 'opacity 0.15s',
  }),

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 },
  stat: { background: '#F9FAFB', borderRadius: 8, padding: 16, border: '1px solid #E5E7EB' },
  statLabel: { fontSize: 12, color: '#6B7280', marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 700, color: '#101828' },
};

// ── Сортування таблиць за будь-якою колонкою (ТЗ 39 п.7.1/7.2) ───────────────
// accessors: { [ключ колонки]: (row) => значення }; клік по заголовку —
// за зростанням, ще клік — за спаданням. Порожні значення завжди в кінці.
function useSort(rows, accessors, initial = null) {
  const [sort, setSort] = useState(initial);   // { key, dir: 1 | -1 } | null
  const toggle = (key) => setSort((s) => (s && s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  let sorted = rows;
  const get = sort && accessors[sort.key];
  if (get) {
    const blank = (v) => v === null || v === undefined || v === '';
    sorted = rows.map((r, i) => [r, get(r), i]).sort((a, b) => {
      const [, x, ia] = a; const [, y, ib] = b;
      if (blank(x) || blank(y)) return blank(x) === blank(y) ? ia - ib : (blank(x) ? 1 : -1);
      const c = (typeof x === 'number' && typeof y === 'number') ? x - y
        : String(x).localeCompare(String(y), 'uk', { numeric: true, sensitivity: 'base' });
      return c !== 0 ? c * sort.dir : ia - ib;
    }).map(([r]) => r);
  }
  return { sorted, sort, toggle };
}

function SortTh({ k, sort, onSort, style, title, children }) {
  const active = sort && sort.key === k;
  return (
    <th style={{ ...style, cursor: onSort ? 'pointer' : style?.cursor, userSelect: 'none' }}
      title={title} onClick={onSort ? () => onSort(k) : undefined}
      aria-sort={active ? (sort.dir > 0 ? 'ascending' : 'descending') : undefined}>
      {children}
      {onSort && <span style={{ marginLeft: 4, opacity: active ? 1 : 0.25 }}>
        {active ? (sort.dir > 0 ? '▲' : '▼') : '↕'}</span>}
    </th>
  );
}

const tsOf = (iso) => { const t = iso ? Date.parse(iso) : NaN; return isNaN(t) ? null : t; };
const numOf = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// ── Broadcast Tab ─────────────────────────────────────────────────────────────
// ── Налаштування акаунтів: маржинальність і плече на всіх (менеджер 13.10) ──
// Одне значення для всіх акаунтів замість перемикачів на сторінці користувача.
// Поки його не збережено, кожен бот бере власні налаштування акаунта.
function AccountsTab() {
  const [data,    setData]    = useState(null);
  const [error,   setError]   = useState('');
  const [mgOnly,  setMgOnly]  = useState(false);
  const [cap,     setCap]     = useState(1.0);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState('');

  const load = useCallback(async () => {
    try {
      const r = await adminApi.getAccountMargin();
      setData(r.data);
      const c = r.data.config;
      setMgOnly(c ? !!c.marginable_only : false);
      setCap(c ? Number(c.leverage_cap) || 1.0 : 1.0);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Не вдалося завантажити');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setSaved('');
    try {
      await adminApi.setAccountMargin({ marginable_only: mgOnly || cap > 1, leverage_cap: cap });
      await load();
      setSaved('Збережено. Діє з наступного запуску робота кожного користувача.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Не вдалося зберегти');
    } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm('Скинути спільне значення? Кожен бот знову братиме власні налаштування акаунта.')) return;
    setSaving(true); setSaved('');
    try { await adminApi.clearAccountMargin(); await load(); setSaved('Скинуто.'); }
    catch (e) { alert(e.response?.data?.detail || 'Не вдалося скинути'); }
    finally { setSaving(false); }
  };

  if (error) return <div style={S.card}><span style={{ color: '#991B1B' }}>{error}</span></div>;
  if (!data) return <div style={S.card}>Завантаження…</div>;

  const isSet = !!data.config;
  const lev   = cap > 1;
  const fmtLev = (v) => (Number(v) > 1 ? `x${Number(v)}` : 'без плеча');
  const fmtCfg = (c) => `${c.marginable_only ? 'лише marginable' : 'усі акції'}, ${fmtLev(c.leverage_cap)}`;
  const label = { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#374151' };

  return (
    <>
      <div style={S.card}>
        <div style={S.cardTitle}>Маржинальність і кредитне плече — одне значення для всіх акаунтів</div>
        <div style={{ fontSize: 13, color: isSet ? '#166534' : '#92400E', marginBottom: 16 }}>
          {isSet
            ? `Діє спільне значення: ${fmtCfg(data.config)}.`
            : 'Спільне значення ще не задане — кожен бот бере власні налаштування акаунта (таблиця нижче).'}
        </div>

        <label style={{ ...label, marginBottom: 14, cursor: lev ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={mgOnly || lev} disabled={lev || saving}
                 onChange={e => setMgOnly(e.target.checked)}
                 style={{ marginTop: 2, width: 16, height: 16 }} />
          <span>
            <b>Купувати лише marginable акції</b> (на які брокер дає кредит)
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, lineHeight: '16px' }}>
              Робот пропускатиме сигнали купівлі по акціях без margin-кредиту IBKR (OTC,
              низьколіквідні). Продажі відкритих позицій не обмежуються.
              {lev && ' З плечем вмикається обовʼязково.'}{' '}
              <a href="/marginality"
                 onClick={(e) => { e.preventDefault();
                                   window.history.pushState({}, '', '/marginality');
                                   window.dispatchEvent(new PopStateEvent('popstate')); }}
                 style={{ color: '#2563EB', textDecoration: 'underline' }}>
                Подивитись список акцій
              </a>
            </div>
          </span>
        </label>

        <div style={{ fontSize: 14, color: '#374151', marginBottom: 6 }}><b>Кредитне плече</b></div>
        <select value={String(cap)} disabled={saving} onChange={e => setCap(Number(e.target.value))}
                style={{ padding: '8px 12px', borderRadius: 8, minWidth: 220, border: '1px solid #E5E7EB',
                         fontSize: 14, fontFamily: 'inherit' }}>
          {[1.0, ...(data.rungs || [])].map(v => (
            <option key={v} value={String(v)}>{v > 1 ? `x${v}` : 'Без кредитного плеча'}</option>
          ))}
        </select>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6, lineHeight: '16px' }}>
          Стеля — x{data.ceiling} (env MARGIN_LEVERAGE_CAP). Плече діє лише на margin-акаунтах IBKR;
          на cash-акаунтах робот його ігнорує.
        </div>
        {lev && (
          <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: '#FEF2F2',
                        border: '1px solid #FECACA', fontSize: 12, lineHeight: '17px', color: '#7F1D1D' }}>
            ⚠️ Плече x{cap} для ВСІХ margin-акаунтів: прибутки й збитки множаться (просадка 10% ≈
            −{Math.round(cap * 10)}% капіталу), IBKR може примусово продати позиції (margin call),
            на позичене нараховується ~6% річних.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={S.btn('primary')} disabled={saving} onClick={save}>
            {saving ? 'Зберігаю…' : 'Зберегти для всіх акаунтів'}
          </button>
          {isSet && (
            <button style={S.btn()} disabled={saving} onClick={reset}>
              Скинути (власні налаштування акаунтів)
            </button>
          )}
          {saved && <span style={{ fontSize: 13, color: '#166534' }}>{saved}</span>}
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 10 }}>
          Застосовується при наступному запуску робота кожного користувача — запущені роботи
          працюють зі старим значенням до перезапуску (вкладка «Контейнери»).
        </div>
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>Акаунти</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead><tr>{['ID', 'Користувач', 'Власне значення акаунта', 'Отримає при наступному запуску'].map(h =>
              <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.user_id}>
                  <td style={S.td}>{u.user_id}</td>
                  <td style={S.td}>{u.email || u.username}</td>
                  <td style={{ ...S.td, color: (isSet || u.has_settings === false) ? '#9CA3AF' : '#374151' }}>
                    {u.has_settings === false ? 'ще не налаштовано' : fmtCfg(u.own)}</td>
                  <td style={{ ...S.td, fontWeight: 600 }}>{fmtCfg(u.effective)}</td>
                </tr>
              ))}
              {!data.users.length && (
                <tr><td style={S.td} colSpan={4}>Немає користувачів</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function BroadcastTab() {
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject]       = useState('');
  const [bodyText, setBodyText]     = useState('');
  const [sel, setSel]               = useState({});   // { [userId]: {email, telegram} }
  const [result, setResult]         = useState(null);
  const [sending, setSending]       = useState(false);
  const [senders, setSenders]       = useState([]);
  const [sender, setSender]         = useState('');

  const load = useCallback(() => {
    adminApi.getBroadcastRecipients()
      .then(r => setRecipients(r.data.recipients || []))
      .catch(() => setRecipients([]));
    adminApi.getBroadcastSenders()
      .then(r => { setSenders(r.data.senders || []); setSender(s => s || r.data.default || ''); })
      .catch(() => setSenders([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (uid, ch) =>
    setSel(p => ({ ...p, [uid]: { ...p[uid], [ch]: !p[uid]?.[ch] } }));

  const selectAll = (ch) => setSel(p => {
    const next = { ...p };
    recipients.forEach(r => {
      const avail = ch === 'email'
        ? (r.news_email && r.email)
        : (r.news_telegram && r.telegram_linked);
      if (avail) next[r.id] = { ...next[r.id], [ch]: true };
    });
    return next;
  });

  const send = async () => {
    const targets = recipients
      .map(r => ({ user_id: r.id, email: !!sel[r.id]?.email, telegram: !!sel[r.id]?.telegram }))
      .filter(t => t.email || t.telegram);
    if (!subject.trim() || !bodyText.trim() || !targets.length) return;
    setSending(true);
    try {
      const r = await adminApi.sendBroadcast({ subject, body: bodyText, targets, sender: sender || 'support' });
      setResult(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Не вдалося надіслати');
    } finally { setSending(false); }
  };

  const inputStyle = { width: '100%', padding: 10, marginBottom: 10,
                       border: '1px solid #E5E7EB', borderRadius: 8, fontFamily: 'inherit' };

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Розсилка оголошень (лише хто дав згоду на новини)</div>
      {/* Телефон: підпис і список переносяться на два рядки, список не ширший за картку. */}
      <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 8px', marginBottom: 10, fontSize: 13, color: '#374151' }}>
        <span style={{ whiteSpace: 'nowrap' }}>Email надсилається з:</span>
        {senders.length ? (
          <select value={sender} onChange={e => setSender(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontFamily: 'inherit',
                     maxWidth: '100%', minWidth: 0, textOverflow: 'ellipsis' }}>
            {senders.map(x => <option key={x.key} value={x.key}>{x.from}</option>)}
          </select>
        ) : <span style={{ color: '#991B1B' }}>поштову скриньку не налаштовано на сервері</span>}
      </label>
      <input placeholder="Тема (для email)" value={subject}
             onChange={e => setSubject(e.target.value)} style={inputStyle} />
      <textarea placeholder="Текст повідомлення" value={bodyText} rows={5}
                onChange={e => setBodyText(e.target.value)} style={inputStyle} />
      <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={S.btn()} onClick={() => selectAll('email')}>Обрати всі Email</button>
        <button style={S.btn()} onClick={() => selectAll('telegram')}>Обрати всі Telegram</button>
        <button style={S.btn('default')} onClick={() => setSel({})}>Скинути</button>
        <button style={S.btn('primary')} disabled={sending} onClick={send}>
          {sending ? 'Надсилаю…' : 'Надіслати'}
        </button>
      </div>
      {result && (
        <div style={{ margin: '8px 0', color: '#166534', fontWeight: 600 }}>
          Надіслано: email {result.sent_email}, telegram {result.sent_telegram}, пропущено {result.skipped}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['ID', 'Юзер', 'Email', 'Telegram'].map(h =>
          <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {recipients.map(r => (
            <tr key={r.id}>
              <td style={S.td}>{r.id}</td>
              <td style={S.td}>{r.display}</td>
              <td style={S.td}>
                {r.news_email && r.email
                  ? <input type="checkbox" checked={!!sel[r.id]?.email}
                           onChange={() => toggle(r.id, 'email')} />
                  : <span style={{ color: '#9CA3AF' }}>—</span>}
              </td>
              <td style={S.td}>
                {r.news_telegram && r.telegram_linked
                  ? <input type="checkbox" checked={!!sel[r.id]?.telegram}
                           onChange={() => toggle(r.id, 'telegram')} />
                  : <span style={{ color: '#9CA3AF' }}>
                      {r.telegram_linked ? 'відписано' : 'не підключено'}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── Users table styles ───────────────────────────────────────────────────────
// Вигляд — як основна таблиця результатів бектестингу (менеджер 16.10): сітка
// клітинок #E2E8F0, світла шапка, жирні заголовки без капсу, рамка картки.
const UT = {
  scroll: { overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 10 },
  checkbox: { width: 16, height: 16, accentColor: '#00A656', cursor: 'pointer', margin: 0 },
  table:  { width: '100%', borderCollapse: 'collapse', borderStyle: 'hidden' },
  // Рядок ≈ дві строки тексту (менеджер 28.10): малі вертикальні відступи,
  // а те, що не влазить, — у ширину (nowrap + елементи в ряд).
  th: {
    padding: '8px 12px', textAlign: 'left', fontSize: 14, fontWeight: 600,
    color: '#334155', background: '#F8FAFC', border: '1px solid #E2E8F0',
    borderBottom: '2px solid #E2E8F0', whiteSpace: 'nowrap',
  },
  td: {
    padding: '4px 12px', fontSize: 14, lineHeight: '20px', color: '#0F172A',
    border: '1px solid #E2E8F0', whiteSpace: 'nowrap', verticalAlign: 'middle',
  },
  // Закріплені колонки «ID» і «Ім'я»: не їдуть при горизонтальному скролі.
  // Ширина ID фіксована — від неї рахується зсув колонки імені.
  stickyId:   { position: 'sticky', left: 0,  zIndex: 1, background: '#fff',
                boxSizing: 'border-box', width: 56, minWidth: 56, maxWidth: 56 },
  stickyName: { position: 'sticky', left: 56, zIndex: 1, background: '#fff',
                boxShadow: 'inset -1px 0 0 #E5E7EB, 4px 0 6px -4px rgba(15,23,31,0.15)' },
  sub:   { fontSize: 12, lineHeight: '16px', color: '#5B646F' },
  money: { fontWeight: 600 },
  dash:  { color: '#9CA3AF' },
  pill: (kind) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px',
    borderRadius: 14, fontSize: 12, fontWeight: 500,
    background: kind === 'green'  ? 'rgba(0,166,86,0.15)'
              : kind === 'red'    ? 'rgba(251,44,54,0.15)'
              : kind === 'yellow' ? '#FEF9C3' : '#F3F4F6',
    color:      kind === 'green'  ? '#00A656'
              : kind === 'red'    ? '#E7000B'
              : kind === 'yellow' ? '#92400E' : '#5B646F',
  }),
};

// Капітал = Внесено + Поповнення / інше + Зароблено.
const EARNED_TOTAL_TITLE = 'Результат угод бота за весь час: закриті угоди + відкриті позиції '
  + 'бота за поточною ціною, мінус комісії й відсотки за плече. Поповнення рахунку '
  + 'сюди не входять.';
const INFLOWS_TITLE = 'Зміна капіталу, яку дав не бот: поповнення й виведення коштів '
  + 'в IBKR, акції, куплені вручну, дивіденди. = Капітал − Внесено − Зароблено.';

const fmtMoney = (v) => {
  if (v == null) return <span style={UT.dash}>—</span>;
  return <span style={UT.money}>
    ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
  </span>;
};

const fmtEarned = (v) => {
  if (v == null) return <span style={UT.dash}>—</span>;
  const n = Number(v);
  return <span style={{ ...UT.money, color: n > 0 ? '#00A656' : n < 0 ? '#E7000B' : '#0F171F' }}>
    {n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
  </span>;
};

const fmtDate = (iso) => {
  if (!iso) return <span style={UT.dash}>—</span>;
  const d = new Date(iso);
  if (isNaN(d)) return <span style={UT.dash}>—</span>;
  const pad = (x) => String(x).padStart(2, '0');
  return (
    <div>
      <div>{pad(d.getDate())}.{pad(d.getMonth() + 1)}.{d.getFullYear()}</div>
      <div style={UT.sub}>{pad(d.getHours())}:{pad(d.getMinutes())}</div>
    </div>
  );
};

// Detail-level → 4 display-flag columns (cumulative levels: 1=сума, 2=+ціна/к-сть, 3=+тікер, 4=+графік, 5=+причина)
const DETAIL_COLS = [
  { level: 2, header: 'Ціна і к-сть акцій',     title: 'Відображати і ціну і кількість акцій' },
  { level: 3, header: 'Назва акцій',            title: 'Відображати і назву акцій' },
  { level: 4, header: 'Графік акцій',           title: 'Відображати і графік акцій' },
  { level: 5, header: 'Причина купівлі/продажу', title: 'Відображати і причину покупки і продажу' },
];

// Реальні кошти за періоди (правило менеджера 28.10.2026, календар Нью-Йорка).
// Закритий період — лише реалізоване; незакритий — + поточне нереалізоване.
// Рахує бекенд: web/backend/services/earned_periods.py.
const EARNED_CLOSED_TITLE = 'Період закрито: лише реалізований прибуток/збиток — продажі, що '
  + 'відбулися в цьому періоді (за мінусом комісій). Акції, які ще тримаємо, сюди не входять.';
const EARNED_OPEN_TITLE = 'Період ще не закрито: реалізований прибуток/збиток продажів цього '
  + 'періоду + нереалізований результат усіх відкритих позицій на зараз (може змінитися).';
const EARNED_COLS = [
  { key: 'earned_last_year',  header: 'Зароблено в минулому році',  title: EARNED_CLOSED_TITLE },
  { key: 'earned_this_year',  header: 'Зароблено в цьому році',     title: EARNED_OPEN_TITLE },
  { key: 'earned_last_month', header: 'Зароблено в минулому місяці', title: EARNED_CLOSED_TITLE },
  { key: 'earned_this_month', header: 'Зароблено в цьому місяці',    title: EARNED_OPEN_TITLE },
];

// Створення / редагування користувача. user=null → новий (пароль обовʼязковий,
// можна одразу дати роль адміна); інакше — правка профілю, порожній пароль = не міняти.
function UserFormModal({ user, onClose, onSaved }) {
  const isNew = !user;
  const [username, setUsername] = useState(user?.username || '');
  const [email,    setEmail]    = useState(user?.email || '');
  const [name,     setName]     = useState(user?.name || '');
  const [password, setPassword] = useState('');
  const [role,     setRole]     = useState('user');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const field = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #D1D5DB',
                  fontSize: 14, boxSizing: 'border-box' };
  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    if (username.trim().length < 3) { setError('Логін має бути не коротшим за 3 символи'); return; }
    if ((isNew || password) && password.length < 6) {
      setError('Пароль має бути не коротшим за 6 символів'); return;
    }
    if (isNew && role === 'admin' &&
        !window.confirm(`Створити «${username.trim()}» з правами адміна? Він отримає доступ до адмін-панелі.`)) return;
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.createUser({ username: username.trim(), email: email.trim(),
                                    name: name.trim(), password, role });
      } else {
        const body = {};
        if (username.trim() !== (user.username || '')) body.username = username.trim();
        if (email.trim() !== (user.email || ''))       body.email = email.trim();
        if (name.trim() !== (user.name || ''))         body.name = name.trim();
        if (password)                                  body.password = password;
        if (Object.keys(body).length) await adminApi.updateUser(user.id, body);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не вдалося зберегти');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={onClose}>
      <form onSubmit={save} onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 440,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#101828' }}>
            {isNew ? 'Новий користувач' : `Редагувати «${user.name || user.username}»`}
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Логін</label>
          <input style={field} value={username} onChange={e => setUsername(e.target.value)}
            autoComplete="off" autoFocus />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Email <span style={{ fontWeight: 400, color: '#9CA3AF' }}>— необовʼязково, теж годиться для входу</span></label>
          <input style={field} type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="off" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Імʼя <span style={{ fontWeight: 400, color: '#9CA3AF' }}>— необовʼязково</span></label>
          <input style={field} value={name} onChange={e => setName(e.target.value)} autoComplete="off" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>
            {isNew ? 'Пароль' : 'Новий пароль'}
            <span style={{ fontWeight: 400, color: '#9CA3AF' }}>
              {isNew ? ' — мінімум 6 символів' : ' — залиште порожнім, щоб не змінювати'}
            </span>
          </label>
          <input style={field} type="text" value={password} onChange={e => setPassword(e.target.value)}
            autoComplete="new-password" />
        </div>
        {isNew && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Роль</label>
            <select style={field} value={role} onChange={e => setRole(e.target.value)}>
              <option value="user">User</option>
              <option value="admin">Адмін</option>
            </select>
          </div>
        )}

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, color: '#991B1B',
                        fontSize: 13, padding: '8px 12px', marginBottom: 14 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            Скасувати
          </button>
          <button type="submit" disabled={saving}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#163382', color: '#fff',
                     fontWeight: 600, cursor: saving ? 'default' : 'pointer', fontSize: 13, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Збереження…' : isNew ? 'Створити' : 'Зберегти'}
          </button>
        </div>
      </form>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);   // true only until the first load — refreshes update in place
  const [error, setError]     = useState('');
  const [confirm, setConfirm] = useState(null); // { type, userId, username }
  const [editing, setEditing] = useState(undefined); // undefined = закрито, null = новий, {..} = правка
  const [prodStrategies, setProdStrategies] = useState([]);
  const [rungs, setRungs] = useState([]);
  useEffect(() => {
    if (VARIANT !== 'trading') return;
    adminApi.getProductionStrategies().then(r => setProdStrategies(r.data.strategies || [])).catch(() => {});
    adminApi.getAccountMargin().then(r => setRungs(r.data.rungs || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.getUsers({ limit: 100, offset: 0 });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function changeRole(user, role) {
    if (role === user.role) return;
    const who = user.name || user.username;
    const what = role === 'admin'
      ? `Зробити «${who}» адміном? Він отримає доступ до адмін-панелі й усіх адмінських функцій.`
      : `Зняти з «${who}» права адміна?`;
    if (!window.confirm(what)) return;
    try {
      await adminApi.updateUser(user.id, { role });
    } catch (e) {
      alert(e.response?.data?.detail || 'Не вдалося змінити роль');
    }
    load();
  }

  async function toggleActive(user) {
    await adminApi.updateUser(user.id, { is_active: !user.is_active });
    load();
  }

  // Click on a display-flag column: check → set level to that column;
  // uncheck → drop to one level below (levels are cumulative).
  // Optimistic update: flip the row locally right away, reload only on failure.
  async function toggleDetailFlag(user, level) {
    const current = user.detail_level ?? 1;
    const next = current >= level ? level - 1 : level;
    setUsers(us => us.map(x => x.id === user.id ? { ...x, detail_level: next } : x));
    try {
      await adminApi.updateUser(user.id, { detail_level: next });
    } catch (e) {
      load();
    }
  }

  // Trading permission: admin on/off switch (users.live_enabled). Default TRUE →
  // a new user may trade right after signup. Users trade LIVE only (no paper),
  // so this is simply "allow this user to use the bot, or not".
  async function toggleTrading(user) {
    const allowed = user.live_enabled !== false;
    setUsers(us => us.map(x => x.id === user.id ? { ...x, live_enabled: !allowed } : x));
    try {
      await adminApi.updateUser(user.id, { live_enabled: !allowed });
    } catch (e) {
      load();
    }
  }

  const tradingStatus = (u) => {
    if (u.live_enabled === false || u.live_blocked) {
      return <span style={UT.pill('red')}>⛔ Заблоковано</span>;
    }
    return <span style={UT.pill('green')}>✅ Дозволено</span>;
  };

  async function deleteUser(userId) {
    await adminApi.deleteUser(userId);
    setConfirm(null);
    load();
  }

  async function stopContainers(userId) {
    await adminApi.stopUserContainers(userId);
    load();
  }

  const containerStatus = (containers, link) => {
    const bot  = containers?.bot;
    const ibgw = containers?.ibgw;
    if (!bot && !ibgw) return <span style={UT.dash}>—</span>;
    // "Бот" reflects the BOT container ONLY. A running IB Gateway means the user
    // connected IBKR (pressed "Підключити") — NOT that the trading bot was started,
    // so it must NOT read as "Працює". Show the intermediate "IBKR підключено".
    if (bot?.status === 'running') {
      // Контейнер Up ≠ звʼязок з брокером (28.10.2026: шлюз 2 год висів на
      // «Existing session detected», а тут стояло «Працює»). Стан — з журналу
      // зʼєднань (останні подія і час — у підказці).
      const hint = link?.event
        ? `${link.event} · ${new Date(link.at).toLocaleString('uk-UA')}` : 'подій немає';
      if (link?.state === 'reconnecting') {
        return <span style={UT.pill('yellow')} title={hint}>● Перепідключення</span>;
      }
      if (link && link.state !== 'connected') {
        return <span style={UT.pill('red')} title={hint}>● Без звʼязку з IBKR</span>;
      }
      return <span style={UT.pill('green')} title={hint}>● Працює</span>;
    }
    if (ibgw?.status === 'running') return <span style={UT.pill('yellow')}>IBKR підключено</span>;
    return <span style={UT.pill('gray')}>Зупинено</span>;
  };

  const detailCheck = (u, col) => {
    if (u.role === 'admin') {
      return (
        <td key={col.level} style={{ ...UT.td, textAlign: 'center' }} title="Адмін завжди бачить усе">
          <input type="checkbox" checked disabled readOnly style={{ ...UT.checkbox, cursor: 'default' }} />
        </td>
      );
    }
    const checked = (u.detail_level ?? 1) >= col.level;
    return (
      <td key={col.level} style={{ ...UT.td, textAlign: 'center' }}
        title={`${col.title} (рівні кумулятивні: зняття вимикає і вищі)`}>
        <input type="checkbox" checked={checked}
          onChange={() => toggleDetailFlag(u, col.level)}
          style={UT.checkbox} />
      </td>
    );
  };

  // Ключ сортування = текст заголовка колонки.
  const botRank = (u) => (u.containers?.bot?.status === 'running' ? 2
    : u.containers?.ibgw?.status === 'running' ? 1 : (u.containers?.bot || u.containers?.ibgw) ? 0 : null);
  const userAccessors = {
    'ID': u => u.id,
    "Ім'я": u => u.name || u.username,
    'Email': u => u.email,
    'Телефон': u => u.phone,
    'Дата реєстрації': u => tsOf(u.created_at),
    'Внесено (вірт.)': u => numOf(u.virtual_deposited),
    'Зароблено (вірт.)': u => numOf(u.virtual_earned),
    'Капітал (вірт.)': u => numOf(u.virtual_total),
    'Внесено': u => numOf(u.real_deposited),
    'Поповнення / інше': u => numOf(u.real_inflows),
    'Зароблено': u => numOf(u.real_earned),
    'Капітал': u => numOf(u.real_total),
    ...Object.fromEntries(EARNED_COLS.map(c => [c.header, u => numOf(u[c.key])])),
    'Роль': u => (u.role === 'admin' ? 'Адмін' : 'User'),
    ...Object.fromEntries(DETAIL_COLS.map(c => [c.header,
      u => (u.role === 'admin' || (u.detail_level ?? 1) >= c.level ? 1 : 0)])),
    'Статус': u => (u.is_active ? 1 : 0),
    'Бот': botRank,
    'Торги': u => (u.role === 'admin' ? null : (u.live_enabled === false || u.live_blocked ? 0 : 1)),
    'Перехід': u => u.bot_config?.transition_mode,
    'Стратегія': u => u.bot_config?.applied?.display_name || u.bot_config?.effective?.display_name,
    'Плече': u => numOf(u.bot_config?.effective_leverage),
  };
  const { sorted: sortedUsers, sort, toggle: onSort } = useSort(users, userAccessors);

  if (loading) return <div style={{ color: '#6B7280', padding: 20 }}>Завантаження юзерів...</div>;
  if (error)   return <div style={{ color: '#EF4444', padding: 20 }}>{error}</div>;

  // Бектестинг (calc) має СВОЇХ користувачів аналізатора: гроші, видимість
  // угод, бот і торги стосуються лише бота — там і показуються.
  const isTrading = VARIANT === 'trading';
  const HEADERS = isTrading ? [
    'ID', "Ім'я", 'Email', 'Телефон', 'Дата реєстрації',
    'Внесено (вірт.)', 'Зароблено (вірт.)', 'Капітал (вірт.)',
    'Внесено', 'Поповнення / інше', 'Зароблено', 'Капітал',
    ...EARNED_COLS.map(c => c.header),
    'Роль',
    ...DETAIL_COLS.map(c => c.header),
    'Статус', 'Бот', 'Торги', 'Перехід', 'Стратегія', 'Плече', 'Дії',
  ] : [
    'ID', "Ім'я", 'Email', 'Дата реєстрації', 'Роль', 'Статус', 'Дії',
  ];

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={S.cardTitle}>Користувачі ({total})</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn('primary')} onClick={() => setEditing(null)}>+ Додати користувача</button>
          <button style={S.btn('default')} onClick={load}>↻ Оновити</button>
        </div>
      </div>

      {editing !== undefined && (
        <UserFormModal user={editing} onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }} />
      )}

      {confirm && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ fontWeight: 600, color: '#991B1B', marginBottom: 8 }}>
            Видалити юзера "{confirm.username}"? Це видалить усі його дані та зупинить контейнери.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btn('danger')} onClick={() => deleteUser(confirm.userId)}>Видалити</button>
            <button style={S.btn()} onClick={() => setConfirm(null)}>Скасувати</button>
          </div>
        </div>
      )}

      <div style={UT.scroll}>
        <table style={UT.table}>
          <thead>
            <tr>
              {HEADERS.map((h, i) => <SortTh key={h} k={h} sort={sort}
                onSort={userAccessors[h] ? onSort : undefined}
                style={i === 0 ? { ...UT.th, ...UT.stickyId, background: UT.th.background, zIndex: 2 }
                     : i === 1 ? { ...UT.th, ...UT.stickyName, background: UT.th.background, zIndex: 2 }
                     : UT.th}
                title={EARNED_COLS.find(c => c.header === h)?.title
                       || (h === 'Поповнення / інше' ? INFLOWS_TITLE
                           : h === 'Зароблено' ? EARNED_TOTAL_TITLE : undefined)}>{h}</SortTh>)}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(u => (
              <tr key={u.id}>
                <td style={{ ...UT.td, ...UT.stickyId }}>{u.id}</td>
                <td style={{ ...UT.td, ...UT.stickyName }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{u.name || u.username}</div>
                  {u.name && u.name !== u.username && <div style={UT.sub}>{u.username}</div>}
                </td>
                <td style={UT.td}>{u.email || <span style={UT.dash}>—</span>}</td>
                {isTrading && <td style={UT.td}>{u.phone || <span style={UT.dash}>—</span>}</td>}
                <td style={UT.td}>{fmtDate(u.created_at)}</td>
                {isTrading && <>
                <td style={UT.td}>{fmtMoney(u.virtual_deposited)}</td>
                <td style={UT.td}>{fmtEarned(u.virtual_earned)}</td>
                <td style={UT.td}>{fmtMoney(u.virtual_total)}</td>
                <td style={UT.td}>{fmtMoney(u.real_deposited)}</td>
                <td style={UT.td} title={INFLOWS_TITLE}>{fmtEarned(u.real_inflows)}</td>
                <td style={UT.td} title={EARNED_TOTAL_TITLE}>{fmtEarned(u.real_earned)}</td>
                <td style={UT.td}>{fmtMoney(u.real_total)}</td>
                {EARNED_COLS.map(c => (
                  <td key={c.key} style={UT.td} title={c.title}>{fmtEarned(u[c.key])}</td>
                ))}
                </>}
                <td style={UT.td}>
                  {/* Роль змінюється тут; власну роль бекенд змінити не дасть. */}
                  <select value={u.role === 'admin' ? 'admin' : 'user'}
                    onChange={e => changeRole(u, e.target.value)}
                    style={{ ...UT.pill(u.role === 'admin' ? 'yellow' : 'gray'),
                             border: '1px solid #E5E7EB', cursor: 'pointer', paddingRight: 4 }}>
                    <option value="user">User</option>
                    <option value="admin">Адмін</option>
                  </select>
                </td>
                {isTrading && DETAIL_COLS.map(col => detailCheck(u, col))}
                <td style={UT.td}>
                  <span style={UT.pill(u.is_active ? 'green' : 'red')}>
                    {u.is_active ? 'Активний' : 'Вимкнений'}
                  </span>
                </td>
                {isTrading && <td style={UT.td}>{containerStatus(u.containers, u.ibkr_link)}</td>}
                {isTrading && <td style={UT.td}>{u.role === 'admin' ? <span style={UT.dash}>—</span> : tradingStatus(u)}</td>}
                {isTrading && <UserBotCells u={u} strategies={prodStrategies} rungs={rungs} onChanged={load} />}
                <td style={UT.td}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button style={S.btn()} onClick={() => setEditing(u)}
                      title="Логін, email, імʼя, пароль">Редагувати</button>
                    {u.role !== 'admin' && (
                      <>
                        <button style={S.btn()} onClick={() => toggleActive(u)}>
                          {u.is_active ? 'Вимкнути' : 'Увімкнути'}
                        </button>
                        {isTrading && <>
                        <button style={S.btn(u.live_enabled === false ? 'primary' : 'default')}
                          onClick={() => toggleTrading(u)}
                          title={u.live_enabled === false
                            ? 'Дозволити цьому юзеру торгувати ботом'
                            : 'Заблокувати торги для цього юзера'}>
                          {u.live_enabled === false ? 'Дозволити торги' : 'Заблокувати торги'}
                        </button>
                        {(u.containers?.bot?.status === 'running' || u.containers?.ibgw?.status === 'running') && (
                          <button style={S.btn('danger')} onClick={() => stopContainers(u.id)}>
                            {u.containers?.bot?.status === 'running' ? 'Стоп бот' : 'Відключити IBKR'}
                          </button>
                        )}
                        </>}
                        <button style={S.btn('danger')}
                          onClick={() => setConfirm({ type: 'delete', userId: u.id, username: u.username })}>
                          Видалити
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Containers Tab ────────────────────────────────────────────────────────────
function ContainersTab() {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [stopping, setStopping]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getContainers();
      setContainers(res.data.containers);
    } catch (e) {
      setContainers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const CONTAINER_COLS = ['Container', 'Type', 'User ID', 'Email', 'Status', 'CPU %', 'RAM MB', 'Created', 'Образ'];
  const containerAccessors = {
    'Container': c => c.name,
    'Type': c => c.type,
    'User ID': c => numOf(c.user_id),
    'Email': c => c.email,
    'Status': c => c.status,
    'CPU %': c => numOf(c.cpu_pct),
    'RAM MB': c => numOf(c.ram_mb),
    'Created': c => tsOf(c.created),
    'Образ': c => (c.image_current == null ? null : c.image_current ? 1 : 0),
  };
  const { sorted: sortedContainers, sort, toggle: onSort } = useSort(containers, containerAccessors);

  async function stopAll() {
    if (!window.confirm('Stop ALL user containers?')) return;
    setStopping(true);
    await adminApi.stopAllContainers();
    setStopping(false);
    load();
  }

  // Оновити бота на новий образ після деплою коду бота. Шлюз не чіпається
  // (2FA не потрібна), позиції бот відновлює з бази. Рестарт посеред сесії
  // губить сьогоднішні годинні бари з памʼяті — краще поза торгами.
  const stale = containers.filter(c => c.type === 'bot' && c.image_current === false);
  const [recreating, setRecreating] = useState(null);   // user_id | 'all' | null

  async function recreateOne(c) {
    if (!window.confirm(
      `Перезапустити ${c.name} (користувач ${c.user_id}) на новому образі?\n\n` +
      'Шлюз IBKR не чіпається, 2FA не потрібна. Позиції бот відновить з бази. ' +
      'Під час торгів бот втратить сьогоднішні годинні бари — краще поза сесією.')) return;
    setRecreating(c.user_id);
    try {
      const r = await adminApi.recreateBot(c.user_id);
      if (!r.data?.ok) window.alert(`Не вдалось: ${r.data?.error || 'невідома помилка'}`);
    } catch (e) {
      window.alert(`Не вдалось: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRecreating(null);
      load();
    }
  }

  async function recreateAll() {
    if (!window.confirm(
      `Перезапустити на новому образі ${stale.length} бот(ів)?\n\n` +
      stale.map(c => `• ${c.name} — користувач ${c.user_id}`).join('\n') +
      '\n\nСеред них можуть бути live-боти на реальних грошах. Шлюзи не чіпаються.')) return;
    setRecreating('all');
    try {
      const r = await adminApi.recreateBots();
      const failed = (r.data?.results || []).filter(x => !x.ok && !x.skipped);
      if (failed.length) window.alert('Не вдалось:\n' + failed.map(x => `користувач ${x.user_id}: ${x.error}`).join('\n'));
    } catch (e) {
      window.alert(`Не вдалось: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRecreating(null);
      load();
    }
  }

  if (loading) return <div style={{ color: '#6B7280', padding: 20 }}>Loading containers...</div>;

  return (
    <div>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12,
                      flexWrap: 'wrap', gap: 8 }}>
          <div style={{ ...S.cardTitle, marginBottom: 0 }}>Running Containers ({containers.length})</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.btn()} onClick={load}>↻ Refresh</button>
            {stale.length > 0 && (
              <button style={S.btn()} onClick={recreateAll} disabled={recreating !== null}>
                {recreating === 'all' ? 'Оновлюю...' : `⟳ Оновити ботів на старому образі (${stale.length})`}
              </button>
            )}
            {containers.length > 0 && (
              <button style={S.btn('danger')} onClick={stopAll} disabled={stopping}>
                {stopping ? 'Stopping...' : '⬛ Stop All'}
              </button>
            )}
          </div>
        </div>

        {containers.length === 0 ? (
          <div style={{ color: '#6B7280', padding: '20px 0', textAlign: 'center' }}>
            No running containers
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {CONTAINER_COLS.map((h, i) => (
                    <SortTh key={h} k={h} sort={sort} onSort={onSort}
                      style={i === 0 ? { ...S.th, ...S.stickyCol, background: S.th.background, zIndex: 2, whiteSpace: 'nowrap' }
                                     : { ...S.th, whiteSpace: 'nowrap' }}>{h}</SortTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedContainers.map(c => (
                  <tr key={c.container_id} style={{ whiteSpace: 'nowrap' }}>
                    <td style={{ ...S.td, ...S.stickyCol }}><code style={{ fontSize: 12 }}>{c.name}</code></td>
                    <td style={S.td}>
                      <span style={S.badge(c.type === 'ibgw' ? 'yellow' : 'green')}>{c.type}</span>
                    </td>
                    <td style={S.td}>{c.user_id}</td>
                    <td style={S.td} title={c.user_name || undefined}>{c.email || '—'}</td>
                    <td style={S.td}>
                      <span style={S.badge(c.status === 'running' ? 'green' : 'red')}>{c.status}</span>
                    </td>
                    <td style={S.td}>{c.cpu_pct}%</td>
                    <td style={S.td}>{c.ram_mb} MB</td>
                    <td style={S.td}>{c.created ? c.created.slice(0, 19) : '—'}</td>
                    <td style={S.td}>
                      {c.image_current === true && <span style={S.badge('green')}>актуальний</span>}
                      {c.image_current === false && (
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                          <span style={S.badge('yellow')}>старий</span>
                          <button style={S.btn()} onClick={() => recreateOne(c)} disabled={recreating !== null}>
                            {recreating === c.user_id ? 'Оновлюю...' : '⟳ Оновити'}
                          </button>
                        </span>
                      )}
                      {c.image_current == null && '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Results Tab (all users) ───────────────────────────────────────────────────
function ResultsTab() {
  const [results, setResults] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterUser, setFilterUser] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50, offset: 0 };
      if (filterUser) params.user_id = parseInt(filterUser);
      const res = await adminApi.getResults(params);
      setResults(res.data.results);
      setTotal(res.data.total);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [filterUser]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={S.cardTitle}>All Backtesting Results ({total})</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            placeholder="Filter by user ID"
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13 }}
          />
          <button style={S.btn()} onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {loading ? <div style={{ color: '#6B7280' }}>Loading...</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['ID', 'User', 'Name', 'Status', 'Win%', 'Monthly%', 'Trades', 'Created'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map(r => {
                const m = r.metrics || {};
                return (
                  <tr key={r.id}>
                    <td style={S.td}>{r.id}</td>
                    <td style={S.td}><strong>{r.username}</strong> <span style={{ color: '#9CA3AF' }}>#{r.user_id}</span></td>
                    <td style={S.td}>{r.name || '—'}</td>
                    <td style={S.td}>
                      <span style={S.badge(r.status === 'done' ? 'green' : r.status === 'failed' ? 'red' : 'yellow')}>
                        {r.status}
                      </span>
                    </td>
                    <td style={S.td}>{m.win_rate != null ? `${m.win_rate.toFixed(1)}%` : '—'}</td>
                    <td style={S.td}>
                      {m.monthly_return != null
                        ? <span style={{ color: m.monthly_return >= 0 ? '#16A34A' : '#DC2626' }}>
                            {m.monthly_return >= 0 ? '+' : ''}{m.monthly_return.toFixed(2)}%
                          </span>
                        : '—'}
                    </td>
                    <td style={S.td}>{r.buy_count || 0}</td>
                    <td style={S.td}>{r.created_at ? r.created_at.slice(0, 16) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Market Data Tab ───────────────────────────────────────────────────────────
// ── Jobs Tab ──────────────────────────────────────────────────────────────────
// ── Як керувати прорахунками: пауза, терміново, скасування ──────────────────
// Правила, які інакше живуть лише в голові того, хто писав механізм. Наслідки
// конкретного рядка показує ще й вікно підтвердження біля кнопки.
const HELP_KEY = 'admin_jobs_help_open';

function JobsHelp() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(HELP_KEY) === '1'; } catch { return false; }
  });
  const toggle = () => {
    const v = !open;
    setOpen(v);
    try { localStorage.setItem(HELP_KEY, v ? '1' : '0'); } catch { /* приватне вікно */ }
  };
  const h = { fontWeight: 700, fontSize: 13, margin: '12px 0 4px', color: '#101828' };
  const li = { margin: '2px 0' };
  return (
    <div style={{ ...S.card, background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '10px 16px', marginBottom: 12 }}>
      <div onClick={toggle} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#163382' }}>
          ℹ️ Як керувати прорахунками: пауза, терміново, скасування
        </span>
        <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>{open ? 'Згорнути ▲' : 'Показати ▼'}</span>
      </div>
      {!open && (
        <div style={{ fontSize: 12, color: '#475467', marginTop: 4 }}>
          Пауза безпечна й продовжується з того самого місця. ⚡ Терміново ставить прорахунок першим у черзі.
          Cancel незворотний.
        </div>
      )}
      {open && (
        <div style={{ fontSize: 12.5, color: '#344054', lineHeight: 1.5 }}>
          <div style={h}>Як парк рахує</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}>Машини беруть роботу з прорахунків <b>по черзі</b>, а не паралельно: спершу той, що запущений раніше.
              Новий короткий прорахунок поруч із великим без паузи чи «⚡ Терміново» чекатиме, доки великий роздасть усю роботу.</li>
            <li style={li}>Виняток: коли хтось на сторінці окремої стратегії натискає «🔄 Перерахувати всі» (періоди) або
              «Драбина варіантів», машини беруть цю роботу першою — поперед будь-яких прорахунків, навіть термінових.
              Вона невелика (зазвичай хвилини), і результат чекає людина; після неї машини повертаються до прорахунків.</li>
            <li style={li}>На сервері одночасно активні <b>не більше 3 задач</b>, разом із перерахунками рядків. Смарт-пошук займає 2.
              Прорахунки на паузі не займають жодної.</li>
          </ul>

          <div style={h}>⏸ Пауза — безпечна</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}>Зберігає місце, дані й навчений порядок відсіву. «Продовжити» йде з того самого місця на тих самих даних.</li>
            <li style={li}>Настає не миттєво: машини доводять до кінця вже взяті блоки (зазвичай хвилини, на важких сітках — до кількох годин).</li>
            <li style={li}>На паузі можна стояти до <b>14 днів</b>. На паузі одночасно може бути скільки завгодно прорахунків.</li>
            <li style={li}>Після «Продовжити» прорахунок стає в кінець черги (якщо не позначений «⚡ Терміново»).</li>
          </ul>

          <div style={h}>⚡ Терміново — першим у черзі</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}>Ставить прорахунок першим у черзі: поки в ньому є нероздана робота, усі вільні машини беруть блоки саме з нього.</li>
            <li style={li}>Інші прорахунки на паузу не стають, але <b>нових блоків не отримують</b> — дораховуються лише ті, що машини вже взяли.
              Тобто фактично вони чекають, доки терміновий роздасть усю роботу.</li>
            <li style={li}>Кількох термінових парк рахує в тому порядку, в якому їх позначили. Позначка зберігається після паузи.</li>
            <li style={li}>Різниця з паузою: інший прорахунок лишається активним і сам підхоплює машини, щойно в терміновому закінчиться нероздана
              робота (на його «хвості»), — машини не простоюють, а після термінового все продовжується без кнопок.
              Пауза потрібна, якщо інший прорахунок треба справді зупинити (наприклад, перед оновленням коду) або звільнити місце серед 3 активних задач.</li>
          </ul>

          <div style={h}>✕ Cancel — незворотний</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}>Пораховані рядки лишаються, решта прорахунку втрачається, продовжити неможливо. Якщо треба лише звільнити машини — пауза.</li>
            <li style={li}><b>Двоетапний пошук на етапі 1:</b> етап 2 не відбудеться. Рядки лишаться з Avg. Yr лише по періодах етапу 1,
              тобто НЕ остаточним, а «Зберегти топ» і «Мін. Avg.Yr» до них не застосуються.</li>
            <li style={li}><b>На етапі 2:</b> дораховані вікна лишаться; недораховані рядки — з Avg. Yr по етапу 1, і ті, що не пройшли б повного набору, не видаляться.</li>
          </ul>

          <div style={h}>Смарт-пошук (позначка «смарт-пошук» у рядку)</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}>Рядки — це його покоління. <b>Пауза, Продовжити, Терміново й Cancel на будь-якому поколінні діють на весь пошук.</b></li>
            <li style={li}>Усі покоління рахуються на одних даних (знімку першого покоління) — і після паузи теж.</li>
            <li style={li}>Разом із двоетапним: коли бюджет вичерпано, з’являється рядок «… · етап 2» — це вже звичайний прорахунок.</li>
          </ul>

          <div style={h}>Коли краще НЕ зупиняти</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li style={li}><b>Cancel на етапі 1 двоетапного</b>, якщо результати потрібні: без етапу 2 таблиця покаже неперевірені числа.</li>
            <li style={li}><b>Довга пауза, під час якої оновлюють код рушія:</b> продовжити можна, але частини до й після паузи
              рахуватимуться різним кодом — «Продовжити» про це попередить.</li>
            <li style={li}>Прорахунок, що майже дійшов до кінця: пауза безпечна, але все одно доведеться чекати, поки машини доведуть свої блоки.</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function JobsTab() {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState({});  // job_id -> true

  const load = useCallback(async () => {
    try {
      const res = await adminApi.getJobs();
      setJobs(res.data.jobs || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh every 5 s while any job is active.
  // Use a ref so the interval callback always sees the latest jobs state.
  const jobsRef = React.useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      const hasActive = jobsRef.current.some(
        j => ['running', 'loading', 'queued', 'stuck', 'orphaned'].includes(j.status)
      );
      if (hasActive) load();
    }, 5000);
    return () => clearInterval(t);
  }, [load]); // eslint-disable-line

  const action = async (jobId, fn, label, message) => {
    if (!window.confirm(message || `${label} job ${jobId}?`)) return;
    setBusy(b => ({ ...b, [jobId]: true }));
    try {
      const res = await fn(jobId);
      // Попередження сервера має дійти до людини, а не осісти в лозі: єдиний
      // випадок, коли воно виникає, — код рушія змінився за час паузи, і рядки
      // однієї сітки виявляються порахованими різним кодом.
      if (res?.data?.warning) alert(`⚠️ ${res.data.warning}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || `Failed: ${label}`);
    } finally {
      setBusy(b => { const n = { ...b }; delete n[jobId]; return n; });
    }
  };

  const statusColor = {
    running: 'green', loading: 'yellow', queued: 'yellow',
    completed: 'green', stuck: 'red', orphaned: 'red',
    failed: 'red', cancelled: 'gray', paused: 'yellow',
  };

  const typeLabel = {
    grid: '🔍 Grid Search',
    recalc_all: '🔄 Recalc All',
    recalc_selected: '🔄 Recalc Selected',
    unknown: '?',
  };

  const fmtAge = (sec) => {
    if (!sec) return '—';
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  };

  const fmtLastUpd = (ts) => {
    if (!ts) return '—';
    const sec = Math.round(Date.now() / 1000 - ts);
    if (sec < 5) return 'just now';
    return `${fmtAge(sec)} ago`;
  };

  const activeCount = jobs.filter(j => ['running','loading','queued','stuck'].includes(j.status)).length;

  // Наслідки дії саме для цього рядка — у вікні підтвердження, бо читають його
  // в мить натискання, а не панель над таблицею.
  const consequences = (job, kind) => {
    const stage = (job.progress || {}).stage;
    const smart = !!job.smart_root;
    const stage2 = stage === 2 || /етап 2/.test(job.name || '');
    const name = job.name || job.batch_id || job.job_id;
    if (kind === 'pause') {
      return [
        `Поставити на паузу «${name}»?`,
        '',
        '• Місце й дані зберігаються: «Продовжити» піде з того самого місця на тих самих даних.',
        '• Машини спершу доведуть до кінця вже взяті блоки (зазвичай хвилини, на важких сітках — до кількох годин), нових не братимуть.',
        '• На паузі можна стояти до 14 днів; після цього продовжити не вийде.',
        '• Після «Продовжити» прорахунок стане в кінець черги парку (якщо не позначений «⚡ Терміново»).',
        smart ? '• Це смарт-пошук: на паузу стане ВЕСЬ пошук — обидва покоління, нових не заводитиметься.' : null,
      ].filter(x => x !== null).join('\n');
    }
    if (kind === 'cancel') {
      return [
        `СКАСУВАТИ «${name}»? Це НЕЗВОРОТНО.`,
        '',
        '• Уже пораховані рядки лишаться в таблиці, решта прорахунку втрачається; продовжити буде неможливо.',
        '• Якщо потрібно лише звільнити машини на час — краще «⏸ Пауза».',
        smart ? '• Це смарт-пошук: скасується ВЕСЬ пошук, разом з усіма поколіннями.' : null,
        (!stage2 && stage === 1) ? '• Двоетапний пошук на ЕТАПІ 1: етап 2 не відбудеться. Рядки лишаться з Avg. Yr лише по періодах етапу 1 (не остаточним), «Зберегти топ» і «Мін. Avg.Yr» до них не застосуються.' : null,
        stage2 ? '• Етап 2: уже дораховані вікна лишаться в рядках; решта рядків лишиться з Avg. Yr лише по періодах етапу 1, а ті, що не пройшли б повного набору, не будуть видалені.' : null,
      ].filter(x => x !== null).join('\n');
    }
    if (kind === 'urgent') {
      return [
        `Позначити «${name}» терміновим?`,
        '',
        '• Поперед нього підуть лише «Перерахувати всі» й «Драбина варіантів» зі сторінок стратегій (це хвилини).',
        '• Він стане першим у черзі: поки в ньому є нероздана робота, вільні машини братимуть блоки лише з нього.',
        '• Інші прорахунки на паузу не стануть, але нових блоків не отримуватимуть — фактично чекатимуть, доки цей роздасть усю роботу.',
        smart ? '• Це смарт-пошук: позначка діє на весь пошук і на його наступні покоління.' : null,
      ].filter(x => x !== null).join('\n');
    }
    if (kind === 'resume') {
      return [
        `Продовжити «${name}»?`,
        '',
        '• Піде з того місця, де зупинився, на тих самих даних.',
        '• Якщо за час паузи оновлювали код рушія — з’явиться попередження: частини до й після паузи рахувались різним кодом.',
        smart ? '• Це смарт-пошук: продовжиться весь пошук.' : null,
      ].filter(x => x !== null).join('\n');
    }
    return null;
  };

  if (loading) return <div style={{ padding: 32, color: '#6B7280' }}>Loading…</div>;
  if (error)   return <div style={{ padding: 32, color: '#EF4444' }}>{error}</div>;

  return (
    <div>
      {/* Header stats */}
      <div style={S.grid}>
        <div style={S.stat}>
          <div style={S.statLabel}>Total jobs</div>
          <div style={S.statValue}>{jobs.length}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLabel}>Active</div>
          <div style={{ ...S.statValue, color: activeCount > 0 ? '#22C55E' : '#101828' }}>{activeCount}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLabel}>Stuck / Orphaned</div>
          <div style={{ ...S.statValue, color: jobs.filter(j => ['stuck','orphaned'].includes(j.status)).length > 0 ? '#EF4444' : '#101828' }}>
            {jobs.filter(j => ['stuck','orphaned'].includes(j.status)).length}
          </div>
        </div>
        <div style={{ ...S.stat, display: 'flex', alignItems: 'center' }}>
          <button style={S.btn('primary')} onClick={load}>↻ Refresh</button>
        </div>
      </div>

      <JobsHelp />

      {jobs.length === 0 && (
        <div style={{ ...S.card, color: '#6B7280', textAlign: 'center', padding: 48 }}>
          No jobs found in Redis
        </div>
      )}

      {jobs.length > 0 && (
        <div style={S.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>User ID</th>
                  <th style={S.th}>Name / Batch</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Progress</th>
                  <th style={S.th}>Age</th>
                  <th style={S.th}>Last update</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const prog     = job.progress || {};
                  const current  = prog.current || 0;
                  const total    = prog.total || 0;
                  const pct      = total > 0 ? Math.round(current / total * 100) : (prog.progress || 0);
                  const isActive = ['running','loading','queued','stuck','orphaned'].includes(job.status);
                  const isBusy   = !!busy[job.job_id];
                  // Пауза є лише в пошуку: у неї має бути що продовжувати —
                  // курсор і черга діапазонів, тобто саме те, чого в перерахунку
                  // немає. Кнопку показуємо тільки там, де вона спрацює.
                  const canPause  = job.batch_id && ['running','loading'].includes(job.status);
                  const isPaused  = job.status === 'paused';
  
                  return (
                    <tr key={job.job_id} style={{ background: job.status === 'stuck' ? '#FFF7ED' : job.status === 'orphaned' ? '#FEF2F2' : 'inherit' }}>
                      <td style={S.td}>{typeLabel[job.type] || job.type}</td>
                      <td style={S.td}>{job.uid ?? '—'}</td>
                      <td style={{ ...S.td, maxWidth: 220 }}>
                        {job.name && <div style={{ fontWeight: 600, marginBottom: 2 }}>{job.name}</div>}
                        {(job.urgent || job.smart_root) && (
                          <div style={{ display: 'flex', gap: 4, marginBottom: 2 }}>
                            {job.urgent && (
                              <span title="Парк роздає цю сітку раніше за звичайні"
                                    style={{ fontSize: 10, fontWeight: 700, color: '#B45309', background: '#FEF3C7', borderRadius: 4, padding: '0 5px' }}>
                                ⚡ терміново
                              </span>
                            )}
                            {job.smart_root && (
                              <span title={`Покоління смарт-пошуку ${job.smart_root.slice(0, 8)}… — пауза, продовження й «терміново» діють на весь пошук`}
                                    style={{ fontSize: 10, fontWeight: 700, color: '#0F766E', background: '#CCFBF1', borderRadius: 4, padding: '0 5px' }}>
                                смарт-пошук
                              </span>
                            )}
                          </div>
                        )}
                        {job.batch_id && (
                          <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>
                            {job.batch_id.slice(0, 8)}…
                          </div>
                        )}
                      </td>
                      <td style={S.td}>
                        <span style={S.badge(statusColor[job.status] || 'gray')}>
                          {job.status === 'stuck' ? '🔴 stuck'
                            : job.status === 'orphaned' ? '👻 orphaned'
                            : job.status === 'paused' ? '⏸ на паузі'
                            : job.status}
                        </span>
                      </td>
                      <td style={{ ...S.td, minWidth: 160 }}>
                        {total > 0 ? (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                              <span>{current.toLocaleString()} / {total.toLocaleString()}</span>
                              <span>{pct}%</span>
                            </div>
                            <div style={{ height: 6, background: '#E5E7EB', borderRadius: 99, overflow: 'hidden' }}>
                              <div style={{
                                height: '100%', borderRadius: 99,
                                width: `${pct}%`,
                                background: job.status === 'stuck' ? '#F97316' : job.status === 'completed' ? '#22C55E' : '#163382',
                                transition: 'width 0.5s',
                              }} />
                            </div>
                          </>
                        ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                      </td>
                      <td style={S.td}>{fmtAge(job.age_sec)}</td>
                      <td style={S.td}>{fmtLastUpd(job.last_updated)}</td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canPause && (
                            <button
                              disabled={isBusy}
                              style={{ ...S.btn('secondary'), opacity: isBusy ? 0.5 : 1 }}
                              title="Зняти з роздачі, зберігши місце. Машини дораховують узяті блоки й нових не беруть."
                              onClick={() => action(job.job_id, adminApi.pauseJob, 'Пауза', consequences(job, 'pause'))}
                            >
                              ⏸ Пауза
                            </button>
                          )}
                          {isPaused && (
                            <button
                              disabled={isBusy}
                              style={{ ...S.btn('primary'), opacity: isBusy ? 0.5 : 1 }}
                              title="Продовжити з того місця, де стало"
                              onClick={() => action(job.job_id, adminApi.resumeJob, 'Продовжити', consequences(job, 'resume'))}
                            >
                              ▶ Продовжити
                            </button>
                          )}
                          {job.batch_id && (isActive || isPaused) && (
                            job.urgent ? (
                              <button
                                disabled={isBusy}
                                style={{ ...S.btn('secondary'), opacity: isBusy ? 0.5 : 1 }}
                                title="Повернути сітку на її звичайне місце в черзі парку"
                                onClick={() => action(job.job_id, adminApi.normalJob, 'Зняти терміновість')}
                              >
                                Зняти ⚡
                              </button>
                            ) : (
                              <button
                                disabled={isBusy}
                                style={{ ...S.btn('secondary'), opacity: isBusy ? 0.5 : 1 }}
                                title="Роздавати цю сітку раніше за звичайні (поперед неї — лише «Перерахувати всі» й «Драбина варіантів» на сторінках стратегій). Першою в черзі: вільні машини беруть блоки з неї, інші прорахунки чекають (без паузи), доки вона роздасть усю роботу. Для смарт-пошуку — на весь пошук."
                                onClick={() => action(job.job_id, adminApi.urgentJob, 'Терміново', consequences(job, 'urgent'))}
                              >
                                ⚡ Терміново
                              </button>
                            )
                          )}
                          {(isActive || isPaused) && (
                            <button
                              disabled={isBusy}
                              style={{ ...S.btn('danger'), opacity: isBusy ? 0.5 : 1 }}
                              onClick={() => action(job.job_id, adminApi.cancelJob, 'Cancel', job.batch_id ? consequences(job, 'cancel') : null)}
                            >
                              ✕ Cancel
                            </button>
                          )}
                          {['stuck', 'orphaned', 'failed'].includes(job.status) && (
                            <button
                              disabled={isBusy}
                              style={{ ...S.btn('primary'), opacity: isBusy ? 0.5 : 1 }}
                              onClick={() => action(job.job_id, adminApi.restartJob, 'Restart')}
                            >
                              ↺ Restart
                            </button>
                          )}
                          {!isActive && !isPaused && !['stuck','orphaned','failed'].includes(job.status) && (
                            <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Strategy Tab ──────────────────────────────────────────────────────────────
const VARIANT = process.env.REACT_APP_VARIANT || 'calc';

// ── Param labels (same terminology as CALC backtesting form) ─────────────────
const PROD_PARAM_LABELS = {
  x_buy:                    'Поріг зростання для входу (Buy%)',
  t_hours_buy:              'Вікно входу (T-Buy, год)',
  buy_window_days:          'Вікно входу (T-Buy, дн)',
  y_sell:                   'Поріг падіння для виходу (Drop%)',
  sell_window_days:         'Вікно виходу (T-Sell, дн)',
  sell_window_hours:        'Вікно виходу (T-Sell, год)',
  hard_stop_pct:            'Стоп-лос (%)',
  hard_stop_cooldown_bars:  'Кулдаун після стоп-лосу (барів)',
  pos_size_pct:             'Розмір позиції (%)',
  pos_size:                 'Розмір позиції (%)',
  max_positions:            'Макс. позицій',
  num_companies:            'Макс. позицій',
  buy_cap_pct:              'Макс. зріст для купівлі (Buy Cap%)',
  overpriced_pct:           'Перекупленість (Overpriced%)',
  reinvest:                 'Реінвестування',
};

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditStrategyModal({ strategy, onClose, onSaved }) {
  const [name, setName]     = React.useState(strategy.name);
  const [params, setParams] = React.useState({ ...strategy.params });
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    if (!name.trim()) { alert('Введіть назву'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/admin/production-strategies/admin-create', {
        name: name.trim(),
        strategy_key: strategy.strategy_key,
        params_json: params,
        metrics_json: strategy.metrics || {},
        notes: `Змінено з "${strategy.name}"`,
      });
      await api.put('/api/admin/active-strategy', { production_strategy_id: res.data.id });
      onSaved(res.data.id);
    } catch (e) {
      alert('Помилка: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#101828' }}>Редагувати стратегію</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
        </div>

        {/* Backtest metrics (read-only) */}
        {strategy.metrics && Object.keys(strategy.metrics).length > 0 && (
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#15803D', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Результати бектесту
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
              {strategy.metrics.win_rate    != null && <span><b style={{ color: '#15803D' }}>WR</b> {strategy.metrics.win_rate}%</span>}
              {strategy.metrics.monthly_pct != null && <span><b style={{ color: '#1D4ED8' }}>Місячний</b> +{strategy.metrics.monthly_pct}%</span>}
              {strategy.metrics.sharpe      != null && <span><b style={{ color: '#92400E' }}>Sharpe</b> {strategy.metrics.sharpe}</span>}
              {strategy.metrics.max_dd      != null && <span><b style={{ color: '#B91C1C' }}>Max DD</b> {strategy.metrics.max_dd}%</span>}
            </div>
          </div>
        )}

        {/* Name */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Назва стратегії
          </label>
          <input value={name} onChange={e => setName(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #D1D5DB', fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>

        {/* Params grid */}
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Параметри</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 24 }}>
          {Object.entries(params).map(([key, val]) => (
            typeof val !== 'object' && (
              <div key={key}>
                <label style={{ display: 'block', fontSize: 11, color: '#6B7280', marginBottom: 3 }}>
                  {PROD_PARAM_LABELS[key] || key}
                </label>
                <input
                  type={typeof val === 'boolean' ? 'checkbox' : 'number'}
                  value={val} step="0.1"
                  onChange={e => setParams(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
            )
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            Скасувати
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#1C69E3', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            {saving ? 'Збереження...' : 'Зберегти та активувати'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Identity-defining params. Used to resolve which card is active when the
// stored active strategy carries no production_strategy_id (e.g. it was set via
// the raw name+params path instead of the "Активувати" button).
const STRATEGY_SIG_KEYS = [
  'x_buy', 'y_sell', 't_hours_buy', 'pos_size', 'max_positions',
  'take_profit_pct', 'take_profit_mode', 'dip_pct', 'dip_filter_enabled',
  'use_peak_window_buy', 'use_peak_window_sell', 'sell_window_days',
  'hard_stop_pct', 'market_cap_min', 'market_cap_max',
];

// True when two param sets agree on every identity key present in BOTH and at
// least a handful actually matched (guards against trivial/near-empty matches).
function strategyParamsMatch(a, b) {
  if (!a || !b) return false;
  let compared = 0;
  for (const k of STRATEGY_SIG_KEYS) {
    if (a[k] == null || b[k] == null) continue;
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) return false;
    compared++;
  }
  return compared >= 4;
}

// Зміна загальної стратегії разом із варіантом для акцій попередньої (п. I
// завдання менеджера, 23.10.2026). Варіант стає загальним; у таблиці
// користувачів його можна перекрити для когось окремо.
function ActivateStrategyModal({ strategy, onClose, onDone }) {
  const [mode, setMode]   = useState('by_old');
  const [busy, setBusy]   = useState(false);
  useEffect(() => {
    adminApi.getTransition().then(r => setMode(r.data.transition_mode || 'by_old')).catch(() => {});
  }, []);
  const go = async (applyNow) => {
    setBusy(true);
    try {
      await adminApi.setTransition(mode);
      await adminApi.setActiveStrategyByProdId(strategy.id);
      let msg = `Стратегію «${strategy.name}» активовано.`;
      if (applyNow) {
        const r = await adminApi.applyStrategyToBots();
        const rs = r.data.results || [];
        const applied = rs.filter(x => x.status === 'applied').length;
        const same    = rs.filter(x => x.status === 'unchanged').length;
        const errs    = rs.filter(x => x.status === 'error');
        msg += ` Запущених ботів: ${rs.length}; перезапущено з новою стратегією: ${applied}`
          + (same ? `; без змін (окрема стратегія або вже ця): ${same}` : '')
          + (errs.length ? `; ПОМИЛКИ: ${errs.map(x => `user ${x.user_id}: ${x.detail}`).join('; ')}` : '')
          + '.';
      } else {
        msg += ' Запущені боти перейдуть на неї при наступному запуску або після «Застосувати».';
      }
      alert(msg);
      onDone();
    } catch (e) {
      alert('Помилка: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const HINT = {
    sell_all: 'Усі акції, куплені за попередніми стратегіями, продаються на першій годинній свічці торгів.',
    by_old:   'Кожна акція продається за правилами тієї стратегії, за якою її купили. Нова стратегія купує на вільні місця.',
    by_new:   'Нова стратегія з першої свічки оцінює старі акції за своїми правилами — частина може продатися одразу.',
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
         onClick={busy ? undefined : onClose}>
      <div style={{ ...S.card, maxWidth: 560, width: '100%' }} onClick={e => e.stopPropagation()}>
        <div style={S.cardTitle}>Активувати «{strategy.name}»</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#101828', margin: '12px 0 8px' }}>
          Дії з наявними акціями, купленими за попередньою стратегією:
        </div>
        {TRANSITION_OPTIONS.map(o => (
          <label key={o.value} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10, cursor: 'pointer' }}>
            <input type="radio" name="transition" checked={mode === o.value} onChange={() => setMode(o.value)}
              style={{ marginTop: 3 }} />
            <span>
              <span style={{ fontSize: 14, color: '#101828' }}>{o.label}</span>
              {o.value === 'by_old' && <span style={{ fontSize: 12, color: '#6B7280' }}> (за замовчуванням)</span>}
              <div style={{ fontSize: 12, color: '#6B7280' }}>{HINT[o.value]}</div>
            </span>
          </label>
        ))}
        <div style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 14px' }}>
          Діє для всіх користувачів, крім тих, кому в таблиці «Користувачі» задано окрему стратегію
          чи окремий варіант переходу. Перезапуск ботів шлюз IBKR не чіпає (2FA не потрібна),
          але краще робити поза торговою сесією.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button style={S.btn()} disabled={busy} onClick={onClose}>Скасувати</button>
          <button style={S.btn()} disabled={busy} onClick={() => go(false)}>Лише активувати</button>
          <button style={S.btn('primary')} disabled={busy} onClick={() => go(true)}>
            {busy ? '…' : 'Активувати і застосувати до запущених ботів'}</button>
        </div>
      </div>
    </div>
  );
}

function ProductionStrategiesSection({ activeProdId, activeStrategy, onActivated }) {
  const [list, setList]             = React.useState([]);
  const [loading, setLoading]       = React.useState(false);
  const [editStrategy, setEditStrategy] = React.useState(null);
  const [toActivate, setToActivate]     = React.useState(null);

  const reload = () => {
    setLoading(true);
    adminApi.getProductionStrategies()
      .then(d => setList(d.data.strategies || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  React.useEffect(reload, []);

  // Resolve the active card. Prefer the explicit production_strategy_id; fall
  // back to name/display_name, then to a param-signature match so a strategy
  // set outside the button flow still shows as active.
  const resolvedActiveId = React.useMemo(() => {
    if (activeProdId != null) return activeProdId;
    if (!activeStrategy || list.length === 0) return null;
    const label = activeStrategy.display_name || activeStrategy.name;
    const byName = list.find(s => s.name === label);
    if (byName) return byName.id;
    const byParams = list.find(s => strategyParamsMatch(activeStrategy.params, s.params));
    return byParams ? byParams.id : null;
  }, [activeProdId, activeStrategy, list]);

  // Активація йде через вікно з вибором варіанта для акцій попередньої стратегії.
  const activate = (id) => setToActivate(list.find(s => s.id === id) || null);

  const del = async (id, name) => {
    if (!window.confirm(`Видалити "${name}"?`)) return;
    await adminApi.deleteProductionStrategy(id).catch(() => {});
    reload();
  };

  return (
    <div>
      {toActivate && (
        <ActivateStrategyModal strategy={toActivate}
          onClose={() => setToActivate(null)}
          onDone={() => { const id = toActivate.id; setToActivate(null); if (onActivated) onActivated(id); }} />
      )}
      {editStrategy && (
        <EditStrategyModal
          strategy={editStrategy}
          onClose={() => setEditStrategy(null)}
          onSaved={(newId) => { setEditStrategy(null); reload(); if (onActivated) onActivated(newId); }}
        />
      )}

      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#101828', marginBottom: 12 }}>
        Стратегії від Аналізатора
        <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 8 }}>(відправлені з CALC-сервера)</span>
      </h3>
      {loading && <div style={{ color: '#6B7280', fontSize: 13 }}>Завантаження...</div>}
      {!loading && list.length === 0 && (
        <div style={{ color: '#9CA3AF', fontSize: 13, padding: '12px 0' }}>
          Поки немає стратегій. Натисніть "→ Bot" в Аналізаторі CALC-сервера.
        </div>
      )}
      {list.map(s => {
        const isActive = resolvedActiveId === s.id;
        return (
          <div key={s.id} style={{
            background: isActive ? '#EFF6FF' : '#F9FAFB',
            border: `1px solid ${isActive ? '#3B82F6' : '#E5E7EB'}`,
            borderRadius: 8, padding: '12px 16px', marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#101828', fontSize: 14 }}>{s.name}</span>
                {isActive && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#DBEAFE', color: '#1D4ED8' }}>
                    ✓ АКТИВНА
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                {s.strategy_key}
                {s.backtest_ref && <span style={{ marginLeft: 8, color: '#9CA3AF' }}>{s.backtest_ref}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, fontSize: 12, flexWrap: 'wrap' }}>
              {s.metrics?.win_rate    != null && <span style={{ color: '#15803D' }}>WR {s.metrics.win_rate}%</span>}
              {s.metrics?.monthly_pct != null && <span style={{ color: '#1D4ED8' }}>+{s.metrics.monthly_pct}%/міс</span>}
              {s.metrics?.sharpe      != null && <span style={{ color: '#92400E' }}>Sh {s.metrics.sharpe}</span>}
              {s.metrics?.max_dd      != null && <span style={{ color: '#B91C1C' }}>DD {s.metrics.max_dd}%</span>}
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{new Date(s.created_at).toLocaleDateString('uk-UA')}</div>
            <button onClick={() => setEditStrategy(s)}
              style={{ background: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB',
                       borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
              ✎ Редагувати
            </button>
            {!isActive && (
              <button onClick={() => activate(s.id)}
                style={{ background: '#1C69E3', color: '#fff', border: 'none',
                         borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Активувати
              </button>
            )}
            {isActive && <span style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 500 }}>Активна</span>}
            <button onClick={() => del(s.id, s.name)}
              style={{ background: 'transparent', color: '#EF4444', border: '1px solid #FCA5A5',
                       borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}

function StrategyTab() {
  const [data, setData]             = useState(null);
  const [name, setName]             = useState('meanreversion');
  const [params, setParams]         = useState({});
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [saved_list, setSavedList]  = useState([]);
  const [activeProdId, setActiveProdId] = useState(null);

  const loadActive = () => {
    adminApi.getActiveStrategy().then(d => {
      setData(d.data);
      setName(d.data.strategy.name);
      setParams(d.data.strategy.params || {});
      setActiveProdId(d.data.strategy.production_strategy_id ?? null);
    });
  };

  useEffect(() => {
    loadActive();
    // Список «★ Збережені з бектестингу» показується лише в бектестингу: у боті
    // таблиця analyzer_results порожня, і запит був зайвим (а падав 500).
    if (VARIANT !== 'trading') adminApi.getSavedStrategies().then(d => setSavedList(d.data.saved || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStrategyChange = (newName) => {
    setName(newName);
    const found = data?.available?.find(s => s.name === newName);
    if (found) setParams({ ...found.default_params });
  };

  const handleSave = async () => {
    setSaving(true);
    await adminApi.setActiveStrategy(name, params);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!data) return <div style={{ padding: 24 }}>Завантаження...</div>;

  const PARAM_LABELS = {
    x_buy:          'Поріг входу (x_buy, %)',
    y_sell:         'Поріг виходу (y_sell, %)',
    hard_stop_pct:  'Стоп-лос (%)',
    pos_size_pct:   'Розмір позиції (%)',
    max_positions:  'Макс. позицій',
  };

  const isTrading = VARIANT === 'trading';

  return (
    <div style={{ padding: 24, maxWidth: isTrading ? 860 : 560 }}>

      {/* TRADING: production strategies front-and-center */}
      {isTrading && (
        <ProductionStrategiesSection
          activeProdId={activeProdId}
          activeStrategy={data.strategy}
          onActivated={(id) => { setActiveProdId(id); loadActive(); }}
        />
      )}

      {/* Saved from backtesting (CALC only — trading DB has no analyzer_results) */}
      {!isTrading && saved_list.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>★ Збережені з бектестингу</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {saved_list.map(s => (
              <div key={s.id} onClick={() => setParams({ ...s.params })}
                style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #E2E8F0',
                         background: '#F8FAFC', cursor: 'pointer', fontSize: 13 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.display_name}</div>
                <div style={{ color: '#6B7280', fontSize: 12 }}>
                  WR: {s.metrics.win_rate}% · Monthly: {s.metrics.monthly_pct}% · MaxDD: {s.metrics.max_dd}% · Sharpe: {s.metrics.sharpe}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>Клік → підставить параметри у форму нижче</div>
        </div>
      )}

      {/* Manual form — CALC only */}
      {!isTrading && (
        <>
          <h3 style={{ marginBottom: 16, fontSize: 15, fontWeight: 600, color: '#374151' }}>Активна стратегія</h3>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#6B7280' }}>Базова стратегія</label>
            <select value={name} onChange={e => handleStrategyChange(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #D1D5DB', fontSize: 14 }}>
              {data.available.map(s => <option key={s.name} value={s.name}>{s.display_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px 16px', marginBottom: 16 }}>
            {Object.entries(params).map(([key, val]) => (
              <div key={key}>
                <label style={{ display: 'block', marginBottom: 3, fontSize: 12, color: '#6B7280' }}>
                  {PARAM_LABELS[key] || key}
                </label>
                <input type="number" value={val} step="0.1"
                  onChange={e => setParams(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
                />
              </div>
            ))}
          </div>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
                     background: saved ? '#22C55E' : '#1C69E3', color: '#fff',
                     fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Збереження...' : saved ? '✓ Збережено' : 'Зберегти'}
          </button>
        </>
      )}
    </div>
  );
}


// ── Main Admin Component ──────────────────────────────────────────────────────
export default function Admin() {
  // Tab is mirrored into the URL hash (#tab=users) so each admin tab has its
  // own address and browser back/forward switches tabs.
  const [activeTab, setActiveTabState] = useState(() => {
    const m = window.location.hash.match(/tab=([a-z]+)/);
    return m ? m[1] : (VARIANT === 'trading' ? 'strategy' : 'users');
  });
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const setActiveTab = (id) => {
    if (activeTab === 'content' && id !== 'content' && !confirmLeaveContentTab()) return;
    setActiveTabState(id);
    window.history.pushState({}, '', `${window.location.pathname}#tab=${id}`);
  };
  useEffect(() => {
    const onPop = () => {
      const m = window.location.hash.match(/tab=([a-z]+)/);
      if (!m) return;
      // «Назад» у браузері з «Контенту» з незбереженими змінами — спитати.
      if (activeTabRef.current === 'content' && m[1] !== 'content' && !confirmLeaveContentTab()) {
        window.history.pushState({}, '', `${window.location.pathname}#tab=content`);
        return;
      }
      setActiveTabState(m[1]);
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
  }, []);

  // Порядок у боті (ТЗ 39 п.7): Стратегія, Контейнери, Логи IBKR, Користувачі,
  // Налаштування акаунтів (13.10), Розсилка, Контент. У бектестингу — Користувачі, Всі результати, Завдання.
  const allTabs = [
    // Лише в боті: у бектестингу «активна стратегія» лягала б у ЙОГО базу, яку
    // бот не читає (стратегія до бота йде кнопкою «→ Bot»). Прибрано 29.09.
    { id: 'strategy',   label: '📈 Стратегія',     variants: ['trading'] },
    // Контейнери ботів живуть на стеку бота — у бектестингу нема чим керувати.
    { id: 'containers', label: '🐳 Контейнери',    variants: ['trading'] },
    { id: 'logs',       label: '🔌 Логи IBKR',     variants: ['trading'] },
    { id: 'users',      label: '👥 Користувачі',        variants: ['calc', 'trading'] },
    { id: 'results',    label: '📊 Всі результати',   variants: ['calc'] },
    { id: 'jobs',       label: '⚙️ Завдання',          variants: ['calc'] },
    { id: 'accounts',   label: '⚙️ Налаштування акаунтів', variants: ['trading'] },
    { id: 'broadcast',  label: '📣 Розсилка',      variants: ['trading'] },
    { id: 'content',    label: '📝 Контент',       variants: ['trading'] },
  ];
  const tabs = allTabs.filter(t => t.variants.includes(VARIANT));
  // Телефон (менеджер 13.10): вісім вкладок у рядок не вміщались і ховались за
  // правим краєм — переносимо на кілька рядків, без зайвих відступів обгортки
  // (AppShell уже дає свій відступ).
  const isMobile = useIsMobile();

  return (
    <div style={isMobile ? { ...S.wrap, padding: '8px 0' } : S.wrap}>
      <div style={{ ...S.title, marginBottom: VARIANT === 'trading' ? 20 : 4 }}>Адмін-панель</div>
      {VARIANT !== 'trading' && (
        <div style={S.sub}>Користувачі аналізатора, усі результати бектестингу та завдання</div>
      )}

      <div style={isMobile ? { ...S.tabs, flexWrap: 'wrap', gap: 6, marginBottom: 16 } : S.tabs}>
        {tabs.map(t => (
          <button key={t.id}
                  style={isMobile ? { ...S.tab(activeTab === t.id), padding: '7px 10px', fontSize: 13, whiteSpace: 'nowrap' }
                                  : S.tab(activeTab === t.id)}
                  onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'users'      && <UsersTab />}
      {activeTab === 'containers' && tabs.some(t => t.id === 'containers') && <ContainersTab />}
      {activeTab === 'results'    && <ResultsTab />}
      {activeTab === 'jobs'       && <JobsTab />}
      {activeTab === 'strategy'   && <StrategyTab />}
      {activeTab === 'accounts'   && tabs.some(t => t.id === 'accounts') && <AccountsTab />}
      {activeTab === 'broadcast'  && <BroadcastTab />}
      {activeTab === 'logs'       && <ConnectionLog />}
      {activeTab === 'content'    && tabs.some(t => t.id === 'content') && <ContentTab />}
    </div>
  );
}
